if (!customElements.get('cartui-slider')) {
  class CartUISlider extends HTMLElement {
    constructor() {
      super();

      this.track = this.querySelector('.cartui-slider__track');
      this.slides = Array.from(this.querySelectorAll('.cartui-slider__slide'));
      this.prevBtn = this.querySelector('.cartui-slider__prev');
      this.nextBtn = this.querySelector('.cartui-slider__next');
      this.navItems = Array.from(this.querySelectorAll('.cartui-slider__nav-item'));

      this.pointerDown = false;
      this.pointerMoved = false;
      this.startX = 0;
      this.deltaX = 0;
      this.translateX = 0;
      this.translateStartX = 0;
      this.slideWidth = 0;
      this.maxTranslate = 0;
      this.slideIndex = 1;
      this.slidesPerPage = 1;

      this.onResize = () => {
        this.updateSizes();
        this.goToSlide(this.slideIndex);
      };
    }

    connectedCallback() {
      if (!this.track || this.slides.length === 0) return;

      this.updateSizes();
      this.updateState();
      window.addEventListener('resize', this.onResize);

      this.prevBtn?.addEventListener('click', () => this.prev());
      this.nextBtn?.addEventListener('click', () => this.next());

      this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.track.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.track.addEventListener('pointerleave', this.onPointerUp.bind(this));
      this.track.addEventListener('pointercancel', this.onPointerUp.bind(this));

      this.navItems.forEach((item) => {
        const index = parseInt(item.getAttribute('data-index') || '1', 10);
        item.addEventListener('click', () => this.goToSlide(index));
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    prev() {
      if (this.slideIndex <= 1) return;
      this.goToSlide(this.slideIndex - 1);
    }

    next() {
      if (this.slideIndex > this.slides.length - this.slidesPerPage) return;
      this.goToSlide(this.slideIndex + 1);
    }

    snap() {
      this.goToSlide(this.slideIndex);
    }

    goToSlide(index) {
      if (!this.track) return;
      this.slideIndex = Math.max(
        1,
        Math.min(index, this.slides.length - this.slidesPerPage + 1),
      );
      this.translateX = -(this.slideIndex - 1) * this.slideWidth;
      this.setTranslateX(this.translateX, true);
      this.updateState();
      this.updateHeight();
    }

    setTranslateX(x, animate = false) {
      if (!this.track) return;

      const clampedX = Math.max(this.maxTranslate, Math.min(0, x));
      this.translateX = clampedX;

      this.track.style.transition = animate ? 'all 0.3s ease' : 'none';
      this.track.style.transform = `translateX(${clampedX}px)`;
    }

    onPointerDown(event) {
      if (!this.track) return;

      this.pointerDown = true;
      this.pointerMoved = false;
      this.startX = event.clientX;
      this.deltaX = 0;
      this.translateStartX = this.translateX;

      this.track.style.transition = 'none';
      this.track.classList.add('is-grabbing');
    }

    onPointerMove(event) {
      if (!this.pointerDown || !this.track) return;

      event.preventDefault();
      event.stopPropagation();

      this.pointerMoved = true;
      this.track.classList.add('is-dragging');

      this.deltaX = event.clientX - this.startX;
      this.setTranslateX(this.translateStartX + this.deltaX);
    }

    onPointerUp(event) {
      if (!this.pointerDown || !this.track) return;

      this.pointerDown = false;
      this.track.classList.remove('is-grabbing');

      if (!this.pointerMoved) return;

      event.preventDefault();
      event.stopPropagation();

      this.track.classList.remove('is-dragging');
      const threshold = this.slideWidth / 6;

      if (Math.abs(this.deltaX) > threshold) {
        this.deltaX < 0 ? this.next() : this.prev();
      } else {
        this.snap();
      }
    }

    updateSizes() {
      if (!this.track || this.slides.length === 0) return;

      this.slideWidth = this.track.clientWidth / this.slidesPerPage;
      this.maxTranslate =
        -(this.slides.length - this.slidesPerPage) * this.slideWidth;

      this.updateHeight();
    }

    updateHeight() {
      if (!this.track || this.slides.length === 0) return;
    
      const start = this.slideIndex - 1;
      const end = start + this.slidesPerPage;
      const visibleSlides = this.slides.slice(start, end);
    
      const heights = visibleSlides.map(slide => slide.offsetHeight);
      const maxHeight = Math.max(...heights);
    
      this.track.style.height = `${maxHeight}px`;
      this.track.style.transition = 'all 0.3s ease';
    }

    updateState() {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.slideIndex <= 1;
      }
      if (this.nextBtn) {
        this.nextBtn.disabled =
          this.slideIndex > this.slides.length - this.slidesPerPage;
      }
      if (this.navItems.length > 0) {
        this.navItems.forEach((item) => {
          const index = parseInt(item.getAttribute('data-index') || '1', 10);
          item.classList.toggle(
            'is-active',
            index >= this.slideIndex &&
              index < this.slideIndex + this.slidesPerPage
          );
        });
      }
    }
  }

  customElements.define('cartui-slider', CartUISlider);
}
